import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Copy, KeyRound } from "lucide-react";

export const ResetLinkDialog = ({ member, onClose }) => {
  const [link, setLink] = useState("");
  const [expires, setExpires] = useState("");
  const [loading, setLoading] = useState(false);

  const close = () => {
    setLink("");
    setExpires("");
    onClose();
  };

  const createLink = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.post(`/organization/members/${member.id}/reset-link`);
      setLink(`${window.location.origin}/forgot-password?token=${data.token}`);
      setExpires(new Date(data.expires_at).toLocaleString());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create reset link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy automatically. Select the link and copy it.");
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-md" data-testid="reset-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-indigo-600" /> Password reset link
          </DialogTitle>
          <DialogDescription>
            {member?.name} can use this link once, within 24 hours, to choose a new password.
            Send it privately; anyone with the link can set the password.
          </DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={link} className="font-mono text-xs" data-testid="reset-link-value" />
              <Button type="button" variant="outline" onClick={copy} data-testid="reset-link-copy-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500" data-testid="reset-link-expiry">Expires {expires}. Creating a new link cancels this one.</p>
          </div>
        ) : (
          <Button onClick={createLink} disabled={loading} className="w-full" data-testid="reset-link-create-btn">
            {loading ? "Creating..." : "Create one-time link"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
